import { motion } from "framer-motion";
import {
  Users,
  ScanLine,
  Wine,
  UtensilsCrossed,
  Radio,
  Megaphone,
  Wrench,
} from "lucide-react";

const services = [
  {
    icon: Users,
    title: "Guest Ushers & Wayfinders",
    desc: "Crowd navigation, aisle discipline, and ticket compliance with a hospitality-first attitude.",
    tags: ["Seating ops", "Wayfinding"],
    span: "md:col-span-2",
  },
  {
    icon: ScanLine,
    title: "Ticket Scanners & Turnstile Ops",
    desc: "High-throughput gate clearance with NFC/RFID scanning and live entry counts.",
    tags: ["Gate sync", "Fast entry"],
    span: "md:col-span-2",
  },
  {
    icon: Wine,
    title: "VIP Hospitality & Suites",
    desc: "Backstage catering, artist lounges, and sponsor suite attendants who stay camera-ready.",
    tags: ["Suites", "Artist liaison"],
    span: "md:col-span-2",
  },
  {
    icon: UtensilsCrossed,
    title: "Concessions & Bar Support",
    desc: "TIPS-certified, high-volume food and beverage crews built for halftime rushes.",
    tags: ["TIPS-certified", "Cash handling"],
    span: "md:col-span-3",
  },
  {
    icon: Radio,
    title: "Crowd Flow & Safety Monitors",
    desc: "Egress control, barricade management, and radio-dispatched roaming teams.",
    tags: ["Egress", "Radio dispatch"],
    span: "md:col-span-3",
  },
  {
    icon: Megaphone,
    title: "Brand Ambassadors & Activations",
    desc: "High-energy staff for sponsor activations, product demos, and lead retrieval.",
    tags: ["Demos", "Lead capture"],
    span: "md:col-span-3",
  },
  {
    icon: Wrench,
    title: "Stagehands & Teardown Crews",
    desc: "Overnight strike, gear staging, and perimeter prep — ready when the lights go down.",
    tags: ["Load-in/out", "Overnight"],
    span: "md:col-span-3",
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono-tactical font-semibold text-sky-400">
            What We Staff
          </p>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
            Every Role Your Event Floor Needs
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            From the first gate scan to the last teardown truck, one crew partner
            covers your entire frontline — trained, uniformed, and briefed on
            your venue before doors open.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={`service-card-${i + 1}`}
              className={`${s.span} group bg-[#131622] border border-white/10 rounded-md p-6 transition-all duration-300 hover:border-sky-500/40 hover:shadow-[0_0_30px_rgba(14,165,233,0.12)] hover:-translate-y-1`}
            >
              <span className="w-11 h-11 rounded-sm bg-sky-500/10 border border-sky-500/25 flex items-center justify-center transition-colors duration-300 group-hover:bg-sky-500/20">
                <s.icon className="w-5 h-5 text-sky-500" />
              </span>
              <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-white">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                {s.desc}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono-tactical text-[10px] uppercase tracking-widest text-slate-400 border border-white/10 rounded-full px-2.5 py-1"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
